import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import Modal from 'components/Luxkit/Modal/Modal'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import { offerShareButtonClick, offerShareComplete } from 'actions/OfferActions'
import { isLoggedIn } from 'selectors/accountSelectors'
import * as Analytics from 'analytics/analytics'
import { shareOfferClickedEvent, shareOfferEvent } from 'analytics/optimizely/events'
import { clientEvent } from 'analytics/snowplow/events'
import { OptimizelyClientSideExperiments, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setOptimizelyExperiment } from 'actions/OptimizelyActions'
import ShareModalContentControl from './ShareModalContentControl'
import ShareModalContentVariant from './ShareModalContentVariant'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'

export const EMAIL_MODE = 'email'

export interface ShareConfig {
  url?: string;
  customerPortalURL?: string;
  memberId?: string;
  brand?: string;
  holidaySynonym?: string;
  image?: App.Image;
  offerId?: string;
  offerLocation?: string;
  pathName?: string;
  region?: string;
  type?: string;
  utmCampaign?: string;
  shareTitle?: string;
  mailToSubject?: string;
  pageId?: string;
  pageType?: string;
  onSend?: (state: { friend_email: string }) => Promise<any>;
  shareMessage?: string;
  mailToObject?: string;
}

interface Props {
  loggedIn?: boolean;
  shareConfig: ShareConfig;
  children?: React.ReactElement;
  offerShareButtonClick: (...params: Parameters<typeof offerShareButtonClick>) => void;
  offerShareComplete: (...params: Parameters<typeof offerShareComplete>) => void;
  onShare?: (network: string) => void;
  mode?: typeof EMAIL_MODE;
}

function ShareModal(props: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [mode, setMode] = useState<typeof EMAIL_MODE | undefined>(props.mode)
  const dispatch = useAppDispatch()

  const openShareModal = () => {
    const { offerShareButtonClick, shareConfig } = props
    if (shareConfig.pageId && shareConfig.pageType) {
      offerShareButtonClick(
        shareConfig.pageId,
        shareConfig.pageType,
      )
    }
    setIsOpen(true)
    Analytics.track('share-offer', shareOfferEvent())
    dispatch(setOptimizelyExperiment(OptimizelyClientSideExperiments.offerShareModal))
  }

  const onShare = (shareMethod: string, shareMethodKey?: string, closeModal: boolean = true) => {
    const { offerShareComplete, shareConfig, onShare } = props
    if (shareConfig.pageId && shareConfig.pageType) {
      offerShareComplete(
        shareConfig.pageId,
        shareConfig.pageType,
        shareMethod,
      )
    }
    Analytics.track('client-event', clientEvent({
      subject: 'share',
      action: shareMethodKey ?? shareMethod,
      category: 'offer_page',
      type: 'interaction',
    }))
    onShare?.(shareMethod)
    if (closeModal) closeShareModal()
    Analytics.track('share-offer-clicked', shareOfferClickedEvent())
  }

  const closeShareModal = () => {
    setIsOpen(false)
    setMode(undefined)
  }

  const mailTo = useMemo(() => {
    if (props.loggedIn) return
    if (!props.shareConfig.url) return
    const body = encodeURIComponent(props.shareConfig.url)
    const subject = props.shareConfig.mailToSubject
    return `mailto:?subject=${subject}&body=${body}`
  }, [props.loggedIn, props.shareConfig.mailToSubject, props.shareConfig.url])

  const copyToClipboard = () => {
    if (props.shareConfig.url) {
      copyValueToClipboard(props.shareConfig.url)
      showSnackbar('Link copied to clipboard', 'success')
    }
    onShare('clipboard', undefined, false)
  }

  const {
    shareConfig,
    children,
  } = props

  const buttonChild = React.Children.only(children)
  const ShareButton = buttonChild ? React.cloneElement(buttonChild, {
    onClick: (e) => {
      if (buttonChild.props.onClick) {
        buttonChild.props.onClick(e)
      }
      openShareModal()
    },
  }) : null

  const isShareModalVariantEnabled = !!useAppSelector((state: App.State) => getOptimizelyExperimentVariation(state, OptimizelyClientSideExperiments.offerShareModal))

  const modalTitle = useMemo(() => {
    if (isShareModalVariantEnabled) {
      return `Share this ${shareConfig.pageType}`
    } else {
      return mode === EMAIL_MODE ? 'Share via email' : 'Share via'
    }
  }, [isShareModalVariantEnabled, mode, shareConfig.pageType])

  const contentProps = {
    closeShareModal,
    copyToClipboard,
    mailTo,
    shareConfig,
    onShare,
    mode,
    setMode,
  }
  return (
    <>
      {ShareButton}
      <Modal
        title={modalTitle}
        isOpen={isOpen}
        onClose={closeShareModal}
      >
        {!isShareModalVariantEnabled && <ShareModalContentControl {...contentProps} />}
        {isShareModalVariantEnabled && <ShareModalContentVariant {...contentProps} />}
      </Modal>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    loggedIn: isLoggedIn(state),
  }
}

const actions = {
  offerShareButtonClick,
  offerShareComplete,
}

export default connect(mapStateToProps, actions)(ShareModal)
